import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, TextField } from 'ui/baseComponents'
import { extractApiErrorMessage } from '../../api/apiErrors'

const HighlightCardForm = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  tags,
}) => {
  const [formData, setFormData] = useState({
    tag_id: '',
    title: '',
    subtitle: '',
    media_url: '',
  })
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (initialValues) {
      setFormData({
        tag_id: initialValues.tag_id || '',
        title: initialValues.title || '',
        subtitle: initialValues.subtitle || '',
        media_url: initialValues.media_url || '',
      })
    } else {
      setFormData({ tag_id: '', title: '', subtitle: '', media_url: '' })
    }
    setError(null)
  }, [initialValues, open])

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = useCallback(async () => {
    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }
    if (!formData.tag_id && !initialValues) {
      setError('Tag is required')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(formData)
    } catch (err) {
      setError(`Failed to save card: ${extractApiErrorMessage(err)}`)
    } finally {
      setSubmitting(false)
    }
  }, [formData, onSubmit, initialValues])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialValues ? 'Edit Highlight Card' : 'New Highlight Card'}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        <Autocomplete
          disableClearable
          value={(tags || []).find((t) => t.id === formData.tag_id) || null}
          onChange={(e, newValue) => {
            setFormData((prev) => ({
              ...prev,
              tag_id: newValue ? newValue.id : '',
            }))
          }}
          options={tags || []}
          getOptionLabel={(t) => `${t.tag} (${t.id})`}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              size="small"
              label="Tag"
              required
              placeholder="Search tags..."
            />
          )}
        />
        <TextField
          label="Title"
          value={formData.title}
          onChange={handleChange('title')}
          required
          size="small"
        />
        <TextField
          label="Subtitle"
          value={formData.subtitle}
          onChange={handleChange('subtitle')}
          multiline
          rows={3}
          size="small"
        />
        <TextField
          label="Media URL"
          value={formData.media_url}
          onChange={handleChange('media_url')}
          size="small"
          placeholder="Background image or video URL"
        />
      </DialogContent>
      <DialogActions>
        <Button type="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default HighlightCardForm
